import * as React from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Avatar,
  Divider,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { authActions } from "../../store/slices/authSlice";
import { LOCALSTORAGE_USER } from "../../constants/localStorageConstants";
import { LOGIN_URL, TRAVELLER_ROUTES } from "../../constants/routeConstants";
import {
  AirportShuttle,
  ChevronLeft,
  MenuOutlined,
  Close,
} from "@mui/icons-material";
import userAvatar from "../../assets/images/userAvatar.png";

export default function Navbar({ collapsed, setCollapsed }) {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const toggleCollapse = () => setCollapsed((prev) => !prev);
  const toggleMobileDrawer = () => setMobileOpen((prev) => !prev);

  const isLoggedIn = user !== null;
  const handleLogout = () => {
    dispatch(authActions.resetState());
    localStorage.removeItem(LOCALSTORAGE_USER);
    navigate(LOGIN_URL);
    if (isMobile) setMobileOpen(false);
  };

  const routes = TRAVELLER_ROUTES.filter(
    (route) => route.auth === isLoggedIn
  ).filter((route) => route.roles.some((x) => user.roles.includes(x)));

  // Mobilni navbar content
  const mobileNavContent = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        bgcolor: "white",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        p: 2,
      }}
    >
      {/* Header sa logom i close dugmetom */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                bgcolor: "#7C8CF8",
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AirportShuttle sx={{ color: "white" }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#5F6AF6",
                letterSpacing: 0.2,
              }}
            >
              BIMS Travel
            </Typography>
          </Box>
          <IconButton onClick={toggleMobileDrawer} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Navigation links */}
        <List sx={{ flexGrow: 1 }}>
          {routes.map((route, index) => (
            <ListItemButton
              key={index}
              component={Link}
              to={route.url}
              onClick={toggleMobileDrawer}
              sx={{
                borderRadius: 1,
                mb: 1,
                bgcolor:
                  location.pathname === route.url ? "#f4f5f7" : "transparent",
                "&:hover": { bgcolor: "#f4f5f7" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {route.icon}
                <ListItemText
                  primary={route.name}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: location.pathname === route.url ? 600 : 400,
                    color: location.pathname === route.url ? "black" : "gray",
                  }}
                />
              </Box>
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* User info section */}
      <Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={userAvatar}
            alt={user?.name || "User"}
            sx={{ width: 40, height: 40 }}
          />
          <Box>
            <Typography
              sx={{ fontSize: 14, fontWeight: 600, color: "black" }}
            >
              {user?.user.firstName} {user?.user.lastName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "gray" }}>
              {user?.roles?.[0] || "Admin"}
            </Typography>
          </Box>
        </Box>

        <Typography
          onClick={handleLogout}
          sx={{
            mt: 2,
            fontSize: 12,
            color: "#5F6AF6",
            textAlign: "center",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Logout
        </Typography>
      </Box>
    </Box>
  );

  // Mobilni top bar
  if (isMobile) {
    return (
      <>
        {/* Fixed top bar */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 64,
            bgcolor: 'white',
            boxShadow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            zIndex: 1100,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                bgcolor: "#7C8CF8",
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AirportShuttle sx={{ color: "white" }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#5F6AF6",
                letterSpacing: 0.2,
                fontSize: '1.1rem',
              }}
            >
              BIMS Travel
            </Typography>
          </Box>

          <IconButton
            onClick={toggleMobileDrawer}
            sx={{ color: "#5F6AF6" }}
          >
            <MenuOutlined />
          </IconButton>
        </Box>

        {/* Drawer */}
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={toggleMobileDrawer}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
            },
          }}
        >
          {mobileNavContent}
        </Drawer>
      </>
    );
  }

  // Desktop navbar - ostaje isti kao što je bio
  return (
    <Box
      sx={{
        width: collapsed ? 72 : 240,
        minHeight: "100vh",
        bgcolor: "white",
        boxShadow: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "fixed",
        left: 0,
        top: 0,
        p: 2,
        zIndex: 1100,
      }}
    >
      {/* Logo section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
          flexDirection: collapsed ? "column-reverse" : "row",
        }}
      >
        <Box
          sx={{
            bgcolor: "#7C8CF8",
            width: 36,
            height: 36,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AirportShuttle sx={{ color: "white" }} />
        </Box>
        {!collapsed && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#5F6AF6",
              letterSpacing: 0.2,
              ml: 1,
              whiteSpace: "nowrap",
            }}
          >
            BIMS Travel
          </Typography>
        )}

        <IconButton
          onClick={toggleCollapse}
          size="small"
          sx={{
            ml: "auto",
            color: "#5F6AF6",
          }}
        >
          {collapsed ? <MenuOutlined sx={{ padding: 0 }} /> : <ChevronLeft />}
        </IconButton>
      </Box>

      {/* Navigation links */}
      <List
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          alignItems: "self-start",
        }}
      >
        {routes.map((route, index) => (
          <ListItemButton
            key={index}
            component={Link}
            to={route.url}
            sx={{
              borderRadius: 1,
              mb: 1,
              flexShrink: 0,
              flexGrow: 0,
              alignItems: "flex-start",
              width: "100%",
              justifyContent: collapsed ? "center" : "unset",
              bgcolor:
                location.pathname === route.url ? "#f4f5f7" : "transparent",
              "&:hover": { bgcolor: "#f4f5f7" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {route.icon}
              {!collapsed && (
                <ListItemText
                  primary={route.name}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: location.pathname === route.url ? 600 : 400,
                    color: location.pathname === route.url ? "black" : "gray",
                  }}
                />
              )}
            </Box>
          </ListItemButton>
        ))}
      </List>

      {/* User info section */}
      {!collapsed && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={userAvatar}
              alt={user?.name || "User"}
              sx={{ width: 40, height: 40 }}
            />
            <Box>
              <Typography
                sx={{ fontSize: 14, fontWeight: 600, color: "black" }}
              >
                {user?.user.firstName} {user?.user.lastName}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "gray" }}>
                {user?.roles?.[0] || "Admin"}
              </Typography>
            </Box>
          </Box>

          <Typography
            onClick={handleLogout}
            sx={{
              mt: 2,
              fontSize: 12,
              color: "#5F6AF6",
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Logout
          </Typography>
        </Box>
      )}
    </Box>
  );
}